import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, CheckCircle, XCircle, Image, Video, Music, FileText, Film } from 'lucide-react';

/**
 * SpriteLoadingIndicator - Shows a subtle toast notification when sprites are being created
 * 
 * @param {Object} props
 * @param {Array} props.loadingItems - Array of items currently being loaded
 *   Each item: { id, name, type, status: 'loading' | 'success' | 'error', errorMessage? }
 */
const SpriteLoadingIndicator = ({ loadingItems = [] }) => {
  // Get icon based on media type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'image':
        return <Image className="w-4 h-4" />;
      case 'video':
        return <Video className="w-4 h-4" />;
      case 'audio':
        return <Music className="w-4 h-4" />;
      case 'text':
        return <FileText className="w-4 h-4" />;
      case 'gif':
        return <Film className="w-4 h-4" />;
      default:
        return <Video className="w-4 h-4" />;
    }
  };

  // Get status icon with muted colors
  const getStatusIcon = (status) => {
    switch (status) {
      case 'loading':
        return <Loader2 className="w-4 h-4 animate-spin text-gray-400" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-gray-400" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-400/70" />;
      default:
        return <Loader2 className="w-4 h-4 animate-spin text-gray-400" />;
    }
  };

  // Get status text
  const getStatusText = (status, type) => {
    switch (status) {
      case 'loading':
        return `Adding ${type || 'media'}...`;
      case 'success':
        return 'Added';
      case 'error':
        return 'Failed';
      default:
        return 'Processing...';
    }
  };

  return (
    <div className="fixed bottom-24 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      <AnimatePresence mode="popLayout">
        {loadingItems.map((item) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ 
              duration: 0.2,
              ease: "easeInOut"
            }}
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-ag-black-800/80 border border-ag-black-400/50 pointer-events-auto"
          >
            {/* Media type icon */}
            <div className="flex-shrink-0 text-gray-500">
              {getTypeIcon(item.type)}
            </div>

            {/* Content */}
            <div className="flex flex-col min-w-0 max-w-[200px]">
              <span className="text-sm text-gray-300 truncate">
                {item.name || 'Media'}
              </span>
              <span className="text-xs text-gray-500">
                {item.errorMessage || getStatusText(item.status, item.type)}
              </span>
            </div>

            {/* Status icon */}
            <div className="flex-shrink-0 ml-2">
              {getStatusIcon(item.status)}
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default SpriteLoadingIndicator;
